/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

/**
 * HomeModel — visor 3D interactivo del Home.
 *
 * Muestra un diseño real (GLB) con la MISMA iluminación del editor
 * (`Editor3D/components/studio.js`) y permite girarlo con el ratón/dedo.
 *
 * Rendimiento: no monta Three.js hasta que la sección entra en viewport
 * (IntersectionObserver) y el código 3D se solicita de forma diferida. Mientras carga —o
 * si el dispositivo no tiene WebGL— se muestra el póster estático.
 *
 * Accesibilidad: con `prefers-reduced-motion` no autogira.
 */
export default function HomeModel({
    src = "/home/model428.glb",
    poster = "/home/keyring-3d.webp",
    alt = "Diseño 3D personalizado, gíralo para verlo",
}) {
    const mountRef = useRef(null);
    const [inView, setInView] = useState(false);
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);
    const reduceMotion = useReducedMotion();

    // Monta el 3D solo cuando el bloque está cerca del viewport.
    useEffect(() => {
        const el = mountRef.current;
        if (!el) return undefined;
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    io.disconnect();
                }
            },
            { rootMargin: "200px", threshold: 0.01 },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    useEffect(() => {
        if (!inView) return undefined;
        let disposed = false;
        let renderer;
        let controls;
        let frameId;
        let resizeObserver;
        const container = mountRef.current;
        const cleanupRef = { current: null };

        (async () => {
            try {
                const THREE = await import("three");
                const { OrbitControls } = await import("three/examples/jsm/controls/OrbitControls.js");
                const { GLTFLoader } = await import("three/examples/jsm/loaders/GLTFLoader.js");
                const { DRACOLoader } = await import("three/examples/jsm/loaders/DRACOLoader.js");
                const studio = await import("../../Customiz3r/Editor3D/components/studio.js");
                if (disposed || !container) return;

                const width = container.clientWidth || 600;
                const height = container.clientHeight || 340;

                renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
                renderer.setSize(width, height);
                renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
                renderer.outputColorSpace = THREE.SRGBColorSpace;
                // Mismo look que el editor: PBR Neutral (color fiel) + sombras.
                renderer.toneMapping = THREE.NeutralToneMapping;
                renderer.toneMappingExposure = 1.0;
                renderer.shadowMap.enabled = true;
                renderer.shadowMap.type = THREE.PCFSoftShadowMap;
                container.appendChild(renderer.domElement);
                renderer.domElement.style.touchAction = "pan-y";

                const scene = new THREE.Scene();
                const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);

                studio.addStudioLights(scene, { bottomLight: false });
                const environmentTexture = studio.addEnvironment(scene, renderer);
                scene.add(studio.createShadowCatcher(400));

                controls = new OrbitControls(camera, renderer.domElement);
                controls.enableDamping = true;
                controls.dampingFactor = 0.08;
                controls.enablePan = false;
                controls.enableZoom = false;
                controls.minPolarAngle = 0.15;
                controls.maxPolarAngle = Math.PI / 2 - 0.05;
                controls.autoRotate = !reduceMotion;
                controls.autoRotateSpeed = 1.6;

                const dracoLoader = new DRACOLoader().setDecoderPath("/draco/");
                const gltfLoader = new GLTFLoader().setDRACOLoader(dracoLoader);

                gltfLoader.load(
                    src,
                    (gltf) => {
                        if (disposed) return;
                        const object = gltf.scene;
                        scene.add(object);
                        studio.applyPrintedMaterial(object);
                        object.traverse((child) => {
                            if (child.isMesh) {
                                child.castShadow = true;
                                child.receiveShadow = true;
                            }
                        });
                        studio.placeOnGround(object);
                        studio.frameCameraOnObject(camera, controls, object, { duration: 0, zoom: 1.35 });
                        setReady(true);
                    },
                    undefined,
                    () => { if (!disposed) setFailed(true); },
                );

                const renderLoop = () => {
                    if (disposed) return;
                    controls.update();
                    renderer.render(scene, camera);
                    frameId = requestAnimationFrame(renderLoop);
                };
                renderLoop();

                resizeObserver = new ResizeObserver(() => {
                    if (disposed || !container) return;
                    const w = container.clientWidth;
                    const h = container.clientHeight;
                    if (w === 0 || h === 0) return;
                    camera.aspect = w / h;
                    camera.updateProjectionMatrix();
                    renderer.setSize(w, h);
                });
                resizeObserver.observe(container);

                // Limpieza guardada para el return.
                cleanupRef.current = () => {
                    resizeObserver?.disconnect();
                    if (frameId) cancelAnimationFrame(frameId);
                    controls?.dispose();
                    dracoLoader.dispose();
                    scene.traverse((child) => {
                        if (child.isMesh) {
                            child.geometry?.dispose();
                            const mats = Array.isArray(child.material) ? child.material : [child.material];
                            mats.forEach((m) => m?.dispose());
                        }
                    });
                    environmentTexture.dispose();
                    renderer?.dispose();
                    if (renderer?.domElement && container?.contains(renderer.domElement)) {
                        container.removeChild(renderer.domElement);
                    }
                };
            } catch {
                if (!disposed) setFailed(true);
            }
        })();

        return () => {
            disposed = true;
            cleanupRef.current?.();
        };
    }, [inView, reduceMotion, src]);

    return (
        <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden border border-black/10 dark:border-white/10 bg-base-300">
            {/* Lienzo 3D */}
            <div
                ref={mountRef}
                className="absolute inset-0"
                role="img"
                aria-label={alt}
            />
            {/* Póster: visible hasta que el modelo está listo, o si falla WebGL */}
            {(!ready || failed) && (
                <img
                    src={poster}
                    alt={alt}
                    className="absolute inset-0 w-full h-full object-contain p-6 pointer-events-none select-none"
                    draggable="false"
                />
            )}
            {/* Pista de interacción, solo cuando ya se puede girar */}
            {ready && !failed && (
                <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[11px] md:text-xs
                    px-2 py-0.5 rounded-full bg-black/40 text-white/90 backdrop-blur-sm pointer-events-none">
                    Arrástralo para girar
                </span>
            )}
        </div>
    );
}
